<template>
  <section>
    <div class="popups">
      <FulfillOrderOverlay
        v-if="selectedOrder"
        :visible.sync="showFulfillOverlay"
        :order="selectedOrder"
        :brand-id="projectForSelectedOrder ? projectForSelectedOrder.brandId : ''"
        :warehouse-id="projectForSelectedOrder ? projectForSelectedOrder.warehouseId : ''"
        @hidden="showFulfillOverlay = false"
        @order-fulfilled="loadItems"
        @label-generated="loadItems"
        @fulfillment-confirmed="loadItems"
      />

      <!-- Bulk Action Queued Modal -->
      <b-modal
        v-model="showBulkResultsModal"
        size="md"
        title="Bulk Action Queued"
        ok-only
        ok-title="Close"
        scrollable
      >
        <div v-if="bulkActionLoading" class="text-center py-4">
          <i class="fa fa-spinner fa-spin fa-2x"></i>
          <div class="mt-2 text-muted">Submitting bulk action...</div>
        </div>

        <div v-else-if="bulkActionResults && bulkActionResults.success" class="text-center py-3">
          <i class="fa fa-check-circle fa-2x text-success mb-2"></i>
          <p>{{ bulkActionResults.message }}</p>
          <p class="text-muted small">You can track the progress in the Bulk Actions section.</p>
        </div>

        <div v-else-if="bulkActionError" class="alert alert-danger">
          {{ bulkActionError }}
        </div>
      </b-modal>

      <!-- Auto-Generate Labels Customization Sidebar -->
      <b-sidebar
        v-model="showAutoGenerateSidebar"
        right
        shadow
        backdrop
        bg-variant="white"
        no-header
        sidebar-class="sidebar-lg"
      >
        <template>
          <div class="container sidebar-header">
            <b-row>
              <div class="col">
                <h5 class="mb-0">Generate Labels</h5>
              </div>
            </b-row>
          </div>
          <div class="px-3 py-3">
            <b-alert show variant="warning">By default, the label is decided through the shipcode mappings, but you can override it below, and select a custom carrier and even specific service</b-alert>
            <b-form-group>
              <b-form-checkbox
                v-model="autoGenerateOptions.overrideShipcodeMappings"
                switch
              >
                Override default label shipcode mappings
              </b-form-checkbox>
            </b-form-group>

            <template v-if="autoGenerateOptions.overrideShipcodeMappings">
              <b-form-group label="Carrier:">
                <b-v-select
                  v-model="autoGenerateOptions.tplCarrierCodeId"
                  :options="autoGenerateCarrierOptions"
                  :searchable="true"
                  @input="autoGenerateOptions.tplCarrierServiceCodeId = null"
                />
              </b-form-group>
              <b-form-group label="Service:">
                <b-v-select
                  v-model="autoGenerateOptions.tplCarrierServiceCodeId"
                  :options="autoGenerateServiceOptions"
                  :searchable="true"
                  :disabled="!autoGenerateOptions.tplCarrierCodeId"
                />
              </b-form-group>
            </template>

            <b-button
              variant="primary"
              block
              @click="executeAutoGenerateFromSidebar"
            >
              Generate Labels
            </b-button>
            <b-button
              variant="outline-secondary"
              block
              class="mt-2"
              @click="showAutoGenerateSidebar = false"
            >
              Cancel
            </b-button>
          </div>
        </template>
      </b-sidebar>

      <b-sidebar
        v-model="showFilters"
        right
        shadow
        backdrop
        bg-variant="white"
        no-header
        sidebar-class="sidebar-lg"
      >
        <template>
          <div class="container sidebar-header">
            <b-row>
              <div class="col">
                <b-button
                  variant="primary"
                  @click="
                    applyFilters();
                    pagination.page = 1;
                    loadItems();
                    showFilters = false;
                  "
                  >Apply</b-button
                >
              </div>
              <div class="col-auto text-right">
                <b-button
                  type="reset"
                  variant="outline-danger"
                  @click="resetFormFilters()"
                >
                  Reset
                </b-button>
                <b-button
                  variant="outline-secondary"
                  class="ml-1"
                  @click="showFilters = false; formFilters = { ...itemsFilters };"
                >
                  Cancel
                </b-button>
              </div>
            </b-row>
          </div>

          <div class="px-3 py-2">
            <b-form-group label="Order Number:">
              <b-form-input v-model="formFilters.orderNumber" />
            </b-form-group>

            <b-form-group label="WMS Transaction ID:">
              <b-form-input v-model="formFilters.fulfillmentOrderId" />
            </b-form-group>

            <b-form-group label="Fulfillment Batch:">
              <b-form-input v-model="formFilters.fulfillmentBatchSearch" />
            </b-form-group>

            <b-form-group label="Storefront:">
              <BsSelectMultiple
                v-model="formFilters.storefrontIds"
                :searchable="true"
                :options="projectsFilter"
              />
            </b-form-group>

            <b-form-group label="Order Status:">
              <BsSelectMultiple
                v-model="formFilters.orderStatus"
                :options="orderStatusOptions"
              />
            </b-form-group>

            <b-form-group label="Fulfillment Status:">
              <BsSelectMultiple
                v-model="formFilters.fulfillmentStatus"
                :options="fulfillmentStatusOptions"
              />
            </b-form-group>

            <b-form-group label="Fulfilled By Service:">
              <BsSelectMultiple
                v-model="formFilters.fulfilledByService"
                :options="fulfilledByServiceOptions"
              />
            </b-form-group>

            <b-form-group label="Ship To Name:">
              <b-form-input v-model="formFilters.shipToName" />
            </b-form-group>

            <b-form-group label="Ship To Street:">
              <b-form-input v-model="formFilters.shipToStreet" />
            </b-form-group>

            <b-form-group label="Ship To Country:">
              <b-v-select
                name="form-input-shipmentAddress-countryCode"
                v-model="formFilters.shipToCountryCode"
                :searchable="true"
                :options="countryCodeListAll"
              />
            </b-form-group>

            <b-form-group label="Ship To State:" v-if="formFilters.shipToCountryCode">
              <b-v-select
                v-if="formFilters.shipToCountryCode == 'US'"
                name="form-input-shipmentAddress-state"
                v-model="formFilters.shipToState"
                :searchable="true"
                :options="USAStateCodeListAll"
              />
              <b-form-input
                v-else
                id="form-input-shipmentAddress-state"
                v-model="formFilters.shipToState"
              />
            </b-form-group>

            <b-form-group label="Order Date From:">
              <b-form-datepicker
                :reset-button="true"
                v-model="formFilters.orderDateStart"
                class="form-control flatpickr-input"
                placeholder="Select date"
                name="orderDateStart"
              />
            </b-form-group>

            <b-form-group label="Order Date To:">
              <b-form-datepicker
                :reset-button="true"
                v-model="formFilters.orderDateEnd"
                class="form-control flatpickr-input"
                placeholder="Select date"
                name="orderDateEnd"
              />
            </b-form-group>
          </div>
        </template>
      </b-sidebar>
    </div>

    <div class="header">
      <div class="header-body">
        <b-row class="align-items-end">
          <div class="col">
            <h1 class="header-title">Fulfillment: Storefront Orders</h1>
            <ActiveFiltersCard
              :itemsFilters="itemsFilters"
              :resetFilterWithKey="resetFilterWithKey"
              :loadItems="loadItems"
            >
              <template v-slot:filterWrap(orderStatus)="data">
                <div v-if="data.item" class="d-inline-block filter-badge mr-1">
                  <span class="badge badge-primary mr-2">
                    <b>Order Status: </b>
                    <span v-if="data.item.length>0">
                      <span v-for="(val, i) in data.item" :key="val">
                        <span v-if="i > 0">, </span>{{ (orderStatusOptions.find(o => o.value === val) || {}).text || val }}
                      </span>
                      <span class="fa fa-times fa-1x ml-2" @click="resetFilterWithKey('orderStatusList'); loadItems();" />
                    </span>
                    <span v-else>All</span>
                  </span>
                </div>
              </template>

              <template v-slot:filterWrap(fulfillmentStatus)="data">
                <div v-if="data.item" class="d-inline-block filter-badge mr-1">
                  <span class="badge badge-primary mr-2">
                    <b>Fulfillment Status: </b>
                    <span v-if="data.item.length>0">
                      <span v-for="(val, i) in data.item" :key="val">
                        <span v-if="i > 0">, </span>{{ (fulfillmentStatusOptions.find(o => o.value === val) || {}).text || val }}
                      </span>
                      <span class="fa fa-times fa-1x ml-2" @click="resetFilterWithKey('fulfillmentStatusList'); loadItems();" />
                    </span>
                    <span v-else>All</span>
                  </span>
                </div>
              </template>

              <template v-slot:filterWrap(fulfilledByService)="data">
                <div v-if="data.item" class="d-inline-block filter-badge mr-1">
                  <span class="badge badge-primary mr-2">
                    <b>Fulfilled By: </b>
                    <span v-if="data.item.length>0">
                    <span v-for="(val, i) in data.item" :key="val">
                      <span v-if="i > 0">, </span>{{ (fulfilledByServiceOptions.find(o => o.value === val) || {}).text || val }}
                    </span>
                    <span class="fa fa-times fa-1x ml-2" @click="resetFilterWithKey('fulfilledByServiceList'); loadItems();" />
                    </span>
                    <span v-else>All</span>
                  </span>
                </div>
              </template>
              <template v-slot:filterWrap(storefrontIds)="data">
                <div v-if="data.item" class="d-inline-block filter-badge mr-1">
                  <span class="badge badge-primary mr-2">
                    <b>Storefronts: </b>
                    <span v-if="data.item.length>0">
                    <span v-for="(val, i) in data.item" :key="val">
                      <span v-if="i > 0">, </span>{{ (myProjects.find(o => o.value === val) || {}).text || val }}
                    </span>
                    <span class="fa fa-times fa-1x ml-2" @click="resetFilterWithKey('storefrontIds'); loadItems();" />
                    </span>
                    <span v-else>All</span>
                  </span>
                </div>
              </template>
            </ActiveFiltersCard>
          </div>
          <div class="col-auto d-flex align-items-center">
            <!-- Bulk Actions dropdown — only shown when orders are selected -->
            <b-dropdown
              v-if="selectedOrderIds.length > 0"
              variant="warning"
              class="mr-2"
              :text="'Bulk Actions (' + selectedOrderIds.length + ')'"
              right
            >
              <b-dropdown-header>{{ selectedOrderIds.length }} order(s) selected</b-dropdown-header>
              <b-dropdown-item @click="runBulkAction('GenerateLabels')">
                <i class="fa fa-tag mr-2"></i>
                Generate Labels
              </b-dropdown-item>
              <b-dropdown-item @click="runBulkAction('DownloadAllLabels')">
                <i class="fa fa-download mr-2"></i>
                Download All Labels
              </b-dropdown-item>
              <b-dropdown-item @click="runBulkAction('VoidAllLabels')">
                <i class="fa fa-ban mr-2"></i>
                Void All Labels
              </b-dropdown-item>
              <b-dropdown-divider />
              <b-dropdown-item @click="runBulkAction('ScheduleConfirmation')">
                <i class="fa fa-clock-o mr-2"></i>
                Schedule Confirmation
              </b-dropdown-item>
              <b-dropdown-item @click="runBulkAction('ConfirmNow')">
                <i class="fa fa-check mr-2"></i>
                Confirm Now
              </b-dropdown-item>
              <b-dropdown-divider />
              <b-dropdown-item @click="selectedOrderIds = []" variant="secondary">
                <i class="fa fa-times mr-2"></i>
                Clear Selection
              </b-dropdown-item>
            </b-dropdown>

            <b-input-group class="mr-2" style="width: 200px;">
              <b-form-input
                v-model="formFilters.fulfillmentBatchSearch"
                placeholder="Batch #"
                :disabled="loadingItems"
                @keyup.enter.native="applyFiltersForIdSearch(); pagination.page = 1; loadItems();"
              />
              <b-input-group-append>
                <b-button
                  variant="outline-secondary"
                  :disabled="loadingItems"
                  @click="applyFiltersForIdSearch(); pagination.page = 1; loadItems();"
                >
                  <i class="fa fa-search"></i>
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <b-input-group class="mr-2" style="width: 200px;">
              <b-form-input
                v-model="formFilters.orderNumber"
                placeholder="Order #"
                :disabled="loadingItems"
                @keyup.enter.native="applyFiltersForIdSearch(); pagination.page = 1; loadItems();"
              />
              <b-input-group-append>
                <b-button
                  variant="outline-secondary"
                  :disabled="loadingItems"
                  @click="applyFilters(); pagination.page = 1; loadItems();"
                >
                  <i class="fa fa-search"></i>
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <b-input-group class="mr-2" style="width: 200px;">
              <b-form-input
                v-model="formFilters.fulfillmentOrderId"
                placeholder="WMS Trans. ID"
                :disabled="loadingItems"
                @keyup.enter.native="applyFilters(); pagination.page = 1; loadItems();"
              />
              <b-input-group-append>
                <b-button
                  variant="outline-secondary"
                  :disabled="loadingItems"
                  @click="applyFilters(); pagination.page = 1; loadItems();"
                >
                  <i class="fa fa-search"></i>
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <b-button
              variant="primary"
              @click="showFilters = !showFilters"
              :disabled="loadingItems"
            >
              <i class="fa fa-filter mr-2"></i>
              Filters
            </b-button>
          </div>
        </b-row>
      </div>
    </div>

    <b-form-checkbox-group
      id="fulfill-bulk-checkbox-group"
      v-model="selectedOrderIds"
      name="fulfill-bulk-orders"
    >
      <TableListing
        :loading-page="loadingItems"
        :failed-loading="failedLoadItems"
        head-variant="dark"
        :items.sync="items"
        :fields.sync="fields"
        :pagination.sync="pagination"
        :options.sync="tableOptions"
        v-on:pagination-changed="loadItems"
      >
        <!-- Select-all checkbox in header -->
        <template #head(orderNumber)>
          <div class="ml-4">
            <input
              type="checkbox"
              class="mr-1 custom-control-input"
              id="fulfill-check-all"
              name="fulfill-check-all"
              v-model="checkAllSelected"
              @change="toggleSelectAll"
            />
            <label for="fulfill-check-all" class="custom-control-label">Order #</label>
          </div>
        </template>

        <template v-slot:cell(orderNumber)="data">
          <b-form-checkbox
            class="mr-1 d-inline-block"
            :value="data.item.orderId"
          />
          <CopyText :text="data.item.orderNumber" />
        </template>

        <template v-slot:cell(fulfillmentOrderId)="data">
          <div>
            <b>Transaction ID: </b><CopyText
              v-if="data.item.fulfillmentOrderId"
              :text="data.item.fulfillmentOrderId"
            />
            <span v-else class="text-muted">—</span>
          </div>
          <div v-if="data.item.fulfillmentBatchCode">
            <i>Batch ID: </i><CopyText
              v-if="data.item.fulfillmentBatchCode"
              :text="data.item.fulfillmentBatchCode"
            />
            <span v-else class="text-muted">—</span>
          </div>
        </template>

        <template v-slot:cell(storefrontId)="data">
          <span v-if="myProjects.find(p => p.id === data.item.storefrontId)">
            {{ myProjects.find(p => p.id === data.item.storefrontId).name }}
          </span>
          <span v-else class="text-muted">{{ data.item.storefrontId }}</span>
        </template>

        <template v-slot:cell(orderDateTimeUtc)="data">
          <span v-if="data.item.orderDateTimeUtc">{{
            data.item.orderDateTimeUtc | moment('MMM Do YY, h:mm a')
          }}</span>
        </template>

        <template v-slot:cell(orderStatus)="data">
          <div>
            <OrderStatusBadge
              :status="data.item.orderStatus"
              :sent-to-fulfill-service="data.item.sentToFulfillService"
            />
          </div>
          <div class="mt-1">
            <FulfillmentStatusBadge :status="data.item.fulfillmentStatus" />
          </div>
        </template>

        <template v-slot:cell(customerNotes)="data">
          <OrderLogButtons
            :customer-notes="data.item.customerNotes"
            :internal-notes="data.item.internalNotes"
            :connect-log="data.item.connectLog"
          />
        </template>

        <template v-slot:cell(shipments)="data">
          <div v-if="data.item.shipments && data.item.shipments.length" style="max-width: 260px;">
            <ShipmentBadge
              v-for="(shipment, i) in data.item.shipments"
              :key="shipment.shipmentId"
              :shipment="shipment"
              :uid="data.item.orderId + '-' + i"
            />
          </div>
          <span v-else class="text-muted">—</span>
        </template>

        <template v-slot:cell(shipTo)="data">
          <div v-if="data.item.shipTo" style="min-width: 160px; font-size: 0.85em; line-height: 1.4;">
            <div class="d-flex align-items-center mb-1">
              <AddressValidationBadge :value="data.item.shipTo.addressVerified" class="mr-1" />
              <strong>{{ data.item.shipTo.name }}</strong>
            </div>
            <div>{{ [data.item.shipTo.street1, data.item.shipTo.street2, data.item.shipTo.street3].filter(Boolean).join(', ') }}</div>
            <div>{{ [data.item.shipTo.country, data.item.shipTo.state, data.item.shipTo.city, data.item.shipTo.postalCode].filter(Boolean).join(', ') }}</div>
          </div>
        </template>

        <template v-slot:cell(orderId)="data">
          <b-button
            size="sm"
            variant="primary"
            @click="openFulfillOverlay(data.item)"
          >
            Fulfill
          </b-button>
          <b-dropdown size="sm" text="More" variant="secondary" class="ml-1">
            <b-dropdown-item
              :to="{
                name: 'Common.OrderDetails',
                query: { id: data.item.orderId }
              }"
            >
              View
            </b-dropdown-item>
            <b-dropdown-item
              :href="data.item.shipments && data.item.shipments.filter(a => a.status != 'Cancelled').length ? '/download/storefront-orders/' + data.item.orderId + '/labels/download-all' : null"
              :disabled="!(data.item.shipments && data.item.shipments.filter(a => a.status != 'Cancelled').length)"
              target="_blank"
            >
              <i class="fa fa-download"></i> Download Labels
            </b-dropdown-item>
            <b-dropdown-item
              :href="$store.getters.getAppUrl + '/download/storefront-orders/' + data.item.orderId + '/customs-documents?documentType=0'"
              target="_blank"
            >
              <i class="fa fa-file-pdf-o"></i> Download Customs Docs
            </b-dropdown-item>
            <b-dropdown-item
              v-if="(data.item.orderStatus == 'pending_fulfillment') && data.item.fulfillmentStatus != 'Confirmed' && data.item.fulfillmentStatus != 'WaitingConfirmation' && data.item.fulfillmentStatus != 'ScheduledAutoconfirm'"
              :disabled="!!(data.item.shipments && data.item.shipments.filter(a => a.status != 'Cancelled').length)"
              @click="ReProcessOrder(data.item)"
            >
              <i class="fa fa-undo"></i> Unallocate &amp; Place Hold
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </TableListing>
    </b-form-checkbox-group>
  </section>
</template>

<script>
import { TableListing, ActiveFiltersCard } from '@/views/components/custom';
import { crudMixinGen, myCommonDataMixin } from '@/helpers/utils/vueMixins.js';
import { mapGetters } from 'vuex';
import FulfillOrderOverlay from '@/views/components/storefronts/orders/FulfillOrderOverlay.vue';
import OrderLogButtons from '@/views/components/storefronts/orders/OrderLogButtons.vue';
import BsSelectMultiple from '@/views/components/BsSelectMultiple.vue';
import ShipmentBadge from '@/views/components/storefronts/orders/ShipmentBadge.vue';
import OrderStatusBadge from '@/views/components/storefronts/orders/OrderStatusBadge.vue';
import FulfillmentStatusBadge from '@/views/components/storefronts/orders/FulfillmentStatusBadge.vue';
import AddressValidationBadge from '@/views/components/storefronts/orders/AddressValidationBadge.vue';
import CopyText from '@/views/components/CopyText.vue';

export default {
  name: 'Orders.ToFulfill.Index',
  mixins: [crudMixinGen('fulfillment/storefront-orders'), myCommonDataMixin],
  components: {
    TableListing,
    ActiveFiltersCard,
    FulfillOrderOverlay,
    OrderLogButtons,
    BsSelectMultiple,
    ShipmentBadge,
    OrderStatusBadge,
    FulfillmentStatusBadge,
    AddressValidationBadge,
    CopyText
  },

  data: () => ({

    showFulfillOverlay: false,
    selectedOrder: null,

    // Bulk action state
    selectedOrderIds: [],
    checkAllSelected: false,
    showBulkResultsModal: false,
    bulkActionLoading: false,
    bulkActionResults: null,
    bulkActionError: null,
    showAutoGenerateSidebar: false,
    autoGenerateOptions: {
      overrideShipcodeMappings: false,
      tplCarrierCodeId: null,
      tplCarrierServiceCodeId: null
    },

    orderStatusOptions: [
      { value: '', text: 'All' },
      { value: 'awaiting_shipment', text: 'Awaiting Shipment' },
      { value: 'on_hold', text: 'On Hold' },
      { value: 'pending_fulfillment', text: 'Pending Fulfillment' },
      { value: 'shipped', text: 'Shipped' },
      { value: 'cancelled', text: 'Cancelled' },
    ],

    fulfilledByServiceOptions: [
      { value: '', text: 'All' },
      { value: 'TPL', text: 'Extensiv' },
      { value: 'ConnectAPI', text: 'Connect' },
      { value: 'Crane', text: 'Crane' },
      { value: 'Ebix', text: 'Ebix' },
      { value: 'Landmark', text: 'Landmark' },
      { value: 'Luxroutage', text: 'Luxroutage' },
      { value: 'Manual', text: 'Manual' },
      { value: 'ShipStation', text: 'ShipStation' }
    ],

    fulfillmentStatusOptions: [
      { value: '', text: 'All' },
      { value: 'NotStarted', text: 'Not Started' },
      { value: 'PickTicketPending', text: 'Pick Ticket Pending' },
      { value: 'PickingInProgress', text: 'Picking In Progress' },
      { value: 'NotAllocated', text: 'Not Allocated' },
      { value: 'PendingFulfillment', text: 'Ready to Label' },
      { value: 'WaitingConfirmation', text: 'Fully Labeled' },
      { value: 'ScheduledAutoconfirm', text: 'Scheduled Autoconfirm' },
      { value: 'Confirmed', text: 'Confirmed' }
    ],

    fields: [
      { key: 'orderNumber', label: 'Order #', sortable: false },
      { key: 'storefrontId', label: 'Storefront', sortable: false },
      { key: 'orderDateTimeUtc', label: 'Order Date', sortable: false },
      { key: 'orderStatus', label: 'Status', sortable: false },
      { key: 'requestedShippingService', label: 'Req. ShipCode', sortable: false },
      { key: 'fulfillmentOrderId', label: 'WMS', sortable: false },
      { key: 'shipments', label: 'Shipments', sortable: false },
      { key: 'shipTo', label: 'Ship To', sortable: false },
      { key: 'customerNotes', label: 'Logs', sortable: false },
      { key: 'orderId', label: 'Action', sortable: false }
    ]
  }),

  computed: {
    ...mapGetters({
      countryCodeList: 'getCountryCodeList',
      USAStateCodeList: 'getUSAStateCodeList'
    }),
    countryCodeListAll() {
      return [{ value: '', text: 'All' }, ...this.countryCodeList];
    },
    USAStateCodeListAll() {
      return [{ value: '', text: 'All' }, ...this.USAStateCodeList];
    },
    projectForSelectedOrder() {
      if (!this.selectedOrder) return null;
      return this.myProjects.find(p => p.id === this.selectedOrder.storefrontId) || null;
    },
    autoGenerateCarrierOptions() {
      var list = [{ value: null, text: '— Select carrier —' }];
      var carriers = this.myTPL && this.myTPL.carriers ? this.myTPL.carriers : [];
      carriers
      .filter(a => a.name == 'GLS Germany' || a.name == 'Deutsche Post DHL') // hard coding this here, because Germany team is the only one using this
      .forEach(function(itm) {
        list.push({ value: itm.id, text: itm.name + ' (' + itm.description + ')' });
      });
      return list;
    },
    autoGenerateServiceOptions() {
      var list = [{ value: null, text: '- Auto Detect -' }];
      var carrierId = this.autoGenerateOptions.tplCarrierCodeId;
      var services = this.myTPL && this.myTPL.shipmentServices ? this.myTPL.shipmentServices : [];
      services
        .filter(function(s) { return !carrierId || s.tplCarrierId === carrierId; })
        .forEach(function(itm) {
          list.push({ value: itm.id, text: itm.description + ' (' + itm.code + ')' });
        });
      return list;
    }
  },

  mounted() {
    this.$store.dispatch('fetchMyTPLData');
    var query = this.$route && this.$route.query;
    if (query && (query.fulfillmentBatchSearch || query.fulfillmentStatus)) {
      var updates = {};
      if (query.fulfillmentStatus) {
        updates.fulfillmentStatus = Array.isArray(query.fulfillmentStatus)
          ? query.fulfillmentStatus
          : [query.fulfillmentStatus];
      }
      if (query.fulfillmentBatchSearch) {
        updates.fulfillmentBatchSearch = query.fulfillmentBatchSearch;
        if (!query.fulfillmentStatus)
        {
          updates.fulfillmentStatus = [];
        }
        updates.orderStatus = [];
      }
      this.formFilters = Object.assign({}, this.formFilters, updates);
      this.applyFilters();
    }
    this.loadItems();
  },

  methods: {
    applyFiltersForIdSearch() {
      if(this.formFilters.fulfillmentBatchSearch || this.formFilters.orderNumber || this.formFilters.fulfillmentOrderId)
      {
        this.formFilters.orderStatus = [];
        this.formFilters.fulfillmentStatus = [];
      }
      this.applyFilters();
    },
    ReProcessOrder(order, warnIfAlloc = true) {
      let txt2 = 'Are you REALLY sure you want to do that?';
      let msgWArning = "This will remove the pending fulfillment status by placing the storefront order on hold, and cancelling the order in the warehouse!";
      if(order.fulfilledByService == 'Crane') {
        msgWArning = "This will remove the pending fulfillment status by placing the storefront order on hold! NOTE: This will NOT cancel the order at Crane! Make sure the order is already cancelled at Crane before using this!";
        txt2 = 'You sure you CANCELLED in Crane?';
      }
      if(!warnIfAlloc) {
        txt2 = 'Already allocated, remember! Are you REALLY sure?';
        msgWArning = "Order was already allocated and is being packed by someone at the warehouse, if you continue, the order will be unallocated and any pick ticket invalidated! You might need to contact someone at the floor to stop packing if you haven't already.";
      }
      this.$swal({
        title: 'Are you sure?',
        text: msgWArning,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Do it.'
      }).then(result => {
        if (result.value) {
            this.$swal({
              title: txt2,
              icon: 'warning',
              showCancelButton: true,
              confirmButtonColor: '#3085d6',
              cancelButtonColor: '#d33',
              confirmButtonText: 'Yes, do it!'
            }).then(result => {
              if (result.value) {
                let swl = this.$swal({
                  title: 'Updating Order!',
                  text: 'Please wait...',
                  icon: 'warning',
                  showCancelButton: false,
                  showConfirmButton: false,
                  allowOutsideClick: false,
                  allowEscapeKey: false,
                  allowEnterKey: false
                });
                this.loadingOrderChange = true;
                this.$http
                  .post('/api/common/orders/' + order.orderId + '/reprocess', {
                    warnIfAllocated: warnIfAlloc
                  })
                  .then(resp => {
                    this.loadingOrderChange = false;
                    swl.close();
                    if(resp.data.success) {
                      this.$swal('Success!', 'Order was placed into on hold. When ready, remove from hold, and it will be resent to the fulfillment service!', 'success');
                      order.orderStatus = 'on_hold';
                      this.loadItems();
                    } else {
                      this.$swal('Success!', 'Something went wrong', 'error');
                    }
                  })
                  .catch(err => {
                      this.loadingOrderChange = false;
                      swl.close();
                      console.error(err);
                      if (err.response && err.response.data.errorMessage) {
                        if(err.response.data.errorMessage == 'This order is already being packed! Are you sure?') {
                          this.ReProcessOrder(order, false);
                        } else {
                          this.$swal({ title: 'Failed!', text: err.response.data.errorMessage, icon: 'error' });
                        }
                      } else {
                        this.$swal({ title: 'Something went wrong!', icon: 'error' });
                      }
                  });
              }
            });
        }
      });
    },
    openFulfillOverlay(item) {
      this.selectedOrder = { ...item, id: item.orderId };
      this.showFulfillOverlay = true;
    },

    toggleSelectAll() {
      if (this.checkAllSelected) {
        this.selectedOrderIds = this.items.map(function(i) { return i.orderId; });
      } else {
        this.selectedOrderIds = [];
      }
    },

    runBulkAction(actionType) {
      if (!this.selectedOrderIds || this.selectedOrderIds.length === 0) {
        this.$swal('No Orders Selected', 'Please select at least one order.', 'warning');
        return;
      }

      var actionLabels = {
        GenerateLabels: 'Generate Labels',
        DownloadAllLabels: 'Download All Labels',
        VoidAllLabels: 'Void All Labels',
        ScheduleConfirmation: 'Schedule Confirmation',
        ConfirmNow: 'Confirm Now'
      };

      var actionNotes = {
        GenerateLabels: 'Clicking Proceed will allow yout to customize th You will be able to download them all as a single PDF file or individually.',
        DownloadAllLabels: 'This will mass download all labels for the selected orders as a single consolidated PDF.',
        VoidAllLabels: 'This will void all labels on the selected orders.',
        ScheduleConfirmation: 'This will schedule the selected orders for auto-confirmation. Their fulfillment status will be set to Scheduled Autoconfirm.',
        ConfirmNow: 'This will immediately confirm the selected orders, mark shipments as created, and trigger order shipped automations.'
      };

      var actionLabel = actionLabels[actionType] || actionType;
      var actionNote = actionNotes[actionType] || '';

      var self = this;

      if (actionType === 'GenerateLabels') {
          self.autoGenerateOptions = { overrideShipcodeMappings: false, tplCarrierCodeId: null, tplCarrierServiceCodeId: null };
          self.showAutoGenerateSidebar = true;
        return;
      }

      this.$swal({
        title: actionLabel,
        html: '<p>' + actionNote + '</p><p class="mt-2"><strong>' + this.selectedOrderIds.length + ' order(s) selected.</strong></p>',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Proceed',
        cancelButtonText: 'Cancel'
      }).then(function(result) {
        if (result.value) {
          self.executeBulkAction(actionType);
        }
      });
    },

    executeAutoGenerateFromSidebar() {
      this.showAutoGenerateSidebar = false;
      var extraParams = {};
      if (this.autoGenerateOptions.overrideShipcodeMappings) {
        extraParams.overrideFilterBy = {
          tplCarrierCodeId: this.autoGenerateOptions.tplCarrierCodeId || null,
          tplCarrierServiceCodeId: this.autoGenerateOptions.tplCarrierServiceCodeId || null
        };
      }
      this.executeBulkAction('AutoGenerateLabels', extraParams);
    },

    executeBulkAction(actionType, extraParams) {
      this.bulkActionResults = null;
      this.bulkActionError = null;
      this.bulkActionLoading = true;
      this.showBulkResultsModal = true;

      var postData = { orderIds: this.selectedOrderIds, actionType: actionType };
      if (extraParams) {
        Object.assign(postData, extraParams);
      }

      var self = this;
      this.$http.post('/api/common/storefront-orders/fulfillment/bulk-action', postData)
        .then(function(response) {
          if (response.data && response.data.success) {
            self.bulkActionResults = response.data;
            self.$root.$emit('storefront-bulk-action-queued');
          } else {
            self.bulkActionError = (response.data && response.data.errorMessage) || 'The bulk action failed.';
          }
        })
        .catch(function(error) {
          self.bulkActionError = (error.response && error.response.data && error.response.data.errorMessage) || 'An unexpected error occurred.';
        })
        .finally(function() {
          self.bulkActionLoading = false;
          self.selectedOrderIds = [];
          self.checkAllSelected = false;
        });
    }
  }
};
</script>
